import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase, CLAVE_SESION } from '../services/supabaseClient';
import { borrarCopiaLocal } from '../services/sinConexion';
import { listarPendientes } from '../services/ventasPendientes';
import { setNegocioId, setCodigoPais, setMoneda } from '../services/negocioActual';
import { codigoTelefonoPorPais } from '../utils/paises';
import { obtenerSuscripcion, esAdminKontrol } from '../services/membresiaService';
import { origenRegistro } from '../utils/origenRegistro';
import { tienePermiso } from '../utils/permisos';

// Sesión del usuario + negocio activo (multi-tenant).
// Al iniciar sesión se busca el negocio del usuario en usuarios_negocios.

const AuthContext = createContext(null);

// Sin internet, Supabase no puede renovar la sesión vencida y responde "sin
// sesión", pero la deja guardada. La usamos para seguir mostrando Kontrol con
// la copia local; al volver la señal, Supabase la renueva sola.
function sesionGuardada() {
  try {
    const s = JSON.parse(localStorage.getItem(CLAVE_SESION) || 'null');
    return s?.user?.id && s?.refresh_token ? s : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [sesion, setSesion] = useState(null);
  const [negocio, setNegocio] = useState(null); // { id, nombre, rol }
  const [cargando, setCargando] = useState(true);
  // 🔑 Recuperar contraseña: al abrir el enlace del correo, Supabase avisa con
  // PASSWORD_RECOVERY y se muestra la pantalla para poner la contraseña nueva.
  const [recuperando, setRecuperando] = useState(() =>
    new URLSearchParams(window.location.search).has('nueva-clave') || /type=recovery/.test(window.location.hash));
  // Recordamos qué usuario ya tiene su negocio cargado, para no repetir la
  // consulta (ni mostrar el "Cargando...") cuando Supabase refresca el token.
  const usuarioCargadoRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const s = data.session || sesionGuardada();
      setSesion(s);
      if (!s) setCargando(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((evento, nuevaSesion) => {
      if (evento === 'PASSWORD_RECOVERY') setRecuperando(true);
      if (!nuevaSesion && evento !== 'SIGNED_OUT' && sesionGuardada()) return;
      setSesion(nuevaSesion);
      if (!nuevaSesion) {
        usuarioCargadoRef.current = null;
        setNegocio(null);
        setNegocioId(null);
        setCargando(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = sesion?.user?.id || null;

  useEffect(() => {
    if (!userId) return;
    // Si ya cargamos el negocio de este usuario, no repetir en cada refresh
    // de token (evita el "Cargando..." y el reinicio de la vista).
    if (usuarioCargadoRef.current === userId) return;
    let activo = true;
    (async () => {
      setCargando(true);
      // El trigger de la BD crea/vincula el negocio al registrarse;
      // reintentamos por si el trigger aún está procesando.
      for (let intento = 0; intento < 5; intento++) {
        const { data } = await supabase
          .from('usuarios_negocios')
          .select('negocio_id, rol, permisos, negocios ( nombre, logo_url, color_principal, slug, onboarding_completado, pais, rubro, modo_viajes, moneda, contenido_activo, vende )')
          .eq('user_id', userId)
          .limit(1)
          .maybeSingle();
        if (!activo) return;
        if (data) {
          setNegocioId(data.negocio_id);
          setCodigoPais(codigoTelefonoPorPais(data.negocios?.pais));
          setMoneda(data.negocios?.moneda || 'HNL');
          setNegocio({
            id: data.negocio_id,
            nombre: data.negocios?.nombre || 'Mi Negocio',
            logo_url: data.negocios?.logo_url || null,
            color_principal: data.negocios?.color_principal || null,
            slug: data.negocios?.slug || null,
            onboarding_completado: data.negocios?.onboarding_completado || null,
            rubro: data.negocios?.rubro || null,
            modo_viajes: data.negocios?.modo_viajes || false,
            contenido_activo: data.negocios?.contenido_activo || false,
            vende: data.negocios?.vende || 'ambos', // "¿Qué vendes?" (sql/144)
            rol: data.rol,
            permisos: data.permisos || {},
          });
          usuarioCargadoRef.current = userId;
          setCargando(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 800));
      }
      if (activo) setCargando(false);
    })();
    return () => { activo = false; };
  }, [userId]);

  // Membresía del negocio (prueba / activa / cortesía / pausada) y si el
  // usuario es admin de Kontrol. Si falla la consulta, la app sigue normal:
  // quien hace cumplir la pausa es la BD.
  const [suscripcion, setSuscripcion] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);
  const negocioId = negocio?.id || null;
  const recargarSuscripcion = async () => {
    try { setSuscripcion(await obtenerSuscripcion()); } catch { /* sin datos */ }
  };
  useEffect(() => {
    if (!negocioId) { setSuscripcion(null); setEsAdmin(false); return; }
    recargarSuscripcion();
    esAdminKontrol().then(setEsAdmin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocioId]);

  // captchaToken: token de Cloudflare Turnstile (Supabase lo valida si el
  // CAPTCHA está activado en Auth; si no, lo ignora).
  const iniciarSesion = (email, password, captchaToken) =>
    supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });

  // versionLegal: versión de Términos/Privacidad aceptada (queda en los
  // metadatos del usuario junto con la confirmación de mayoría de edad).
  // invitacion: token del enlace de "Mi equipo"; la BD une a la persona a ese
  // negocio en vez de crearle uno nuevo (sql/083).
  const registrarse = (email, password, nombreNegocio, captchaToken, versionLegal, invitacion) =>
    supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          nombre_negocio: nombreNegocio,
          mayor_de_edad: true,
          acepto_terminos_version: versionLegal,
          acepto_terminos_en: new Date().toISOString(),
          ...(invitacion ? { invitacion } : {}),
          ...(!invitacion && origenRegistro() ? { origen: origenRegistro() } : {}),
        },
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

  // Manda el correo con el enlace para cambiar la contraseña.
  const pedirRecuperacion = (email, captchaToken) =>
    supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/?nueva-clave`,
      ...(captchaToken ? { captchaToken } : {}),
    });

  // Guarda la contraseña nueva (ya con la sesión que abrió el enlace).
  const cambiarClave = async (password) => {
    const r = await supabase.auth.updateUser({ password });
    if (!r.error) {
      setRecuperando(false);
      window.history.replaceState(null, '', window.location.pathname);
    }
    return r;
  };

  const cerrarSesion = async () => {
    // Las ventas hechas sin internet quedan guardadas en este dispositivo y se
    // suben cuando esta misma persona vuelva a entrar.
    const pendientes = listarPendientes(negocio?.id).length;
    if (pendientes && !window.confirm(`Tienes ${pendientes === 1 ? '1 venta' : `${pendientes} ventas`} sin subir (hechas sin internet). Quedan guardadas en este dispositivo y se suben cuando vuelvas a entrar con tu cuenta. ¿Cerrar sesión?`)) return undefined;
    await borrarCopiaLocal();
    return supabase.auth.signOut();
  };

  // Refleja cambios del negocio (ej. nombre nuevo) en la sesión, para que
  // el header y demás se actualicen sin recargar.
  const actualizarNegocioLocal = (cambios) =>
    setNegocio((prev) => (prev ? { ...prev, ...cambios } : prev));

  // Si la dueña cambia los permisos, se aplican cuando esta persona vuelve a la
  // pestaña de Kontrol (sin cerrar sesión).
  useEffect(() => {
    if (!negocioId || !userId) return undefined;
    const refrescar = async () => {
      if (document.visibilityState !== 'visible') return;
      const { data } = await supabase.from('usuarios_negocios').select('rol, permisos')
        .eq('user_id', userId).eq('negocio_id', negocioId).maybeSingle();
      if (data) setNegocio((prev) => (prev ? { ...prev, rol: data.rol, permisos: data.permisos || {} } : prev));
    };
    document.addEventListener('visibilitychange', refrescar);
    window.addEventListener('focus', refrescar);
    return () => { document.removeEventListener('visibilitychange', refrescar); window.removeEventListener('focus', refrescar); };
  }, [negocioId, userId]);

  // Vendedor/a: vende y atiende, pero no ve costos, ganancias ni administración.
  const esVendedora = negocio?.rol === 'vendedora';
  // ¿Puede hacer X? La dueña siempre; el equipo según los permisos que le dio (sql/088).
  const puede = (clave) => tienePermiso(negocio, clave);

  return (
    <AuthContext.Provider value={{ sesion, negocio, cargando, iniciarSesion, registrarse, cerrarSesion, recuperando, pedirRecuperacion, cambiarClave, actualizarNegocioLocal, suscripcion, recargarSuscripcion, esAdmin, esVendedora, puede }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
